import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService, ResponseObject } from '../auth.service';
import { ServiceBase } from '../service.base';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';
import { PlateformCurrencyExchangeRate } from '../Wix-Service/wix.service';
import { GalleryConsignmentProduct } from '../consignment.service';

@Injectable({
  providedIn: 'root'
})
export class ShopifyService extends ServiceBase {
    
  private api = '/Shopify'; 

  constructor(
    private http: HttpClient,
    authService: AuthService
  ) {
    super(authService);
  }

  /**
   * Connect with a Shopify account using store URL
   * @param storeUrl - The URL of the Shopify store
   * @returns Observable of ResponseObject
   */
  public connectWithShopifyAccount(storeUrl: string) {
    const httpOptions = this.RequestHeaders();
    const method: string = '/login';
    const url: string = environment.BASE_API_PATH + this.api + method;
    console.log('store url' + storeUrl);
    return this.http.post<ResponseObject>(url, {storeUrl}, httpOptions);
  }

  /**
   * Handle app installation from Shopify Partner Dashboard
   * @param hmac - HMAC verification parameter
   * @param shop - Shopify store domain
   * @param host - Host parameter from Shopify
   * @param timestamp - Timestamp of the request
   * @returns Observable of ResponseObject
   */
  public onInstallAppFromShopifyPartnerDashboard(hmac: string, shop: string, host: string, timestamp: string) {
    const httpOptions = this.RequestHeaders();

    // Build URL with query string parameters
    const params = new HttpParams()
        .set('hmac', hmac)
        .set('shop', shop)
        .set('host', host)
        .set('timestamp', timestamp);

    const url = `${environment.BASE_API_PATH}${this.api}/auth?${params.toString()}`;

    return this.http.get<ResponseObject>(url, httpOptions);
  }

  /**
   * Sync a gallery product with Shopify
   * @param storeId - The Shopify store ID
   * @param productId - The product ID to sync
   * @param consignmentId - The consignment ID
   * @param manualRate - Whether to use manual exchange rate
   * @returns Observable of ResponseObject
   */
  public onProductSyncShopify(storeId: string, productId: string, consignmentId: string, manualRate: boolean): Observable<ResponseObject> {
    const httpOptions = this.RequestHeaders();
    const method = `/sync-gallery-product/${storeId}/${productId}/${consignmentId}/${manualRate}`;
    const url = environment.BASE_API_PATH + this.api + method;
    return this.http.post<ResponseObject>(url, {}, httpOptions);
  }

  /**
   * Check if user has a connected Shopify account
   * @returns Observable of ShopifyOAuthToken
   */
  public hasAccountOnShopify() {
    const httpOptions = this.RequestHeaders();
    const method: string = '/account-exists';
    const url: string = environment.BASE_API_PATH + this.api + method;
    return this.http.get<ShopifyOAuthToken>(url, httpOptions);
  }

  /**
   * Handle Shopify OAuth callback
   * @param code - Authorization code from Shopify
   * @param state - State parameter for OAuth flow
   * @param shop - Shopify store domain
   * @param host - Host parameter from Shopify
   * @returns Observable of ResponseObject
   */
  public onShopifyAccountCallBack(code: string, state: string, shop: string, host: string) {
    const method: string = '/token-callback';
    const url: string = `${environment.BASE_API_PATH}${this.api}${method}/${code}/${state}/${shop}/${host}`;
    const httpOptions = this.RequestHeaders();
    return this.http.post<ResponseObject>(url, {}, httpOptions);
  }

  /**
   * Get all connected Shopify accounts
   * @returns Observable of ShopifyStoreAccount array
   */
  public getShopifyAccounts(): Observable<ShopifyStoreAccount[]> {
    const httpOptions = this.RequestHeaders();
    const method: string = '/stores';
    const url: string = environment.BASE_API_PATH + this.api + method;
    return this.http.get<ShopifyStoreAccount[]>(url, httpOptions);
  }

  /**
   * Disconnect a Shopify account
   * @param id - The account ID to disconnect
   * @returns Observable of ResponseObject
   */
  public disconnectShopifyAccount(id: string): Observable<ResponseObject> {
    const httpOptions = this.RequestHeaders();
    const method = `/disconnect/${id}`;
    const url = `${environment.BASE_API_PATH}${this.api}${method}`;
    return this.http.put<ResponseObject>(url, {}, httpOptions);
  }

  /**
   * Remove synced products by their IDs
   * @param productIds - Array of product IDs to remove
   * @returns Observable of ResponseObject
   */
  public removeSyncedProductById(productIds: string[]): Observable<ResponseObject> {
    const httpOptions = {
      ...this.RequestHeaders(),
      body: {
        productSyncedOnPlatformIds: productIds
      }
    };
    const method: string = '/synced-products';
    const url: string = environment.BASE_API_PATH + this.api + method;
    return this.http.delete<ResponseObject>(url, httpOptions);
  }

  /**
   * Get all synced Shopify products
   * @returns Observable of GalleryConsignmentProduct array
   */
  public getShopifyProducts(): Observable<GalleryConsignmentProduct[]> {
    const httpOptions = this.RequestHeaders();
    const method: string = '/synced-products';
    const url: string = environment.BASE_API_PATH + this.api + method;
    return this.http.get<GalleryConsignmentProduct[]>(url, httpOptions);
  }

  /**
   * Sync an artist product with Shopify
   * @param productId - The product ID to sync
   * @param storeId - The Shopify store ID
   * @param isManualSync - Whether to use manual sync settings
   * @returns Observable of ResponseObject
   */
  public artistOnProductSyncShopify(productId: string, storeId: string, isManualSync: boolean): Observable<ResponseObject> {
    const httpOptions = this.RequestHeaders();
    const method = `/sync-artist-product/${storeId}/${productId}/${isManualSync}`;
    const url = environment.BASE_API_PATH + this.api + method;
    return this.http.post<ResponseObject>(url, {}, httpOptions);
  }

  /**
   * Get store currency with exchange rate for a Shopify store
   * @param id - The store ID
   * @returns Observable of PlateformCurrencyExchangeRate
   */
  public storeCurrencyofShopifyWithExchangeRate(id: string): Observable<PlateformCurrencyExchangeRate> {
    const httpOptions = this.RequestHeaders();
    const url = `${environment.BASE_API_PATH}${this.api}/store/${id}/currency-exchange`;
    return this.http.get<PlateformCurrencyExchangeRate>(url, httpOptions);
  }

  /**
   * Get all synced Shopify products (alternative method)
   * @returns Observable of GalleryConsignmentProduct array
   */
  public getShopifySyncProducts(): Observable<GalleryConsignmentProduct[]> {
    const httpOptions = this.RequestHeaders();
    const method: string = '/synced-products';
    const url: string = environment.BASE_API_PATH + this.api + method;
    return this.http.get<GalleryConsignmentProduct[]>(url, httpOptions);
  }
}

/**
 * Represents Shopify account details
 */
export class ShopifyAccountDetails {
  Shop!: ShopifyStoreAccount;
  Error!: null;
  ShopifyShopUrls!: ShopifyShopUrls;
}


export interface ShopifyStoreAccount {
  Id: string;
  UserId: string;
  AccessTokken: string | null;
  IsActive: boolean;
  CreatedDate: string; // ISO Date string
  OrderFulfilledWebhookId: number;
  ShopUpdateWebhookId: number;
  StoreUrl: string;
  AdminPanelUrl: string;
  StoreDomain: string | null;
  StoreCurrency: string | null;
  StoreName?: string;
  StoreEmail: string | null;
  StoreAddress1: string | null;
  StoreAddress2: string | null;
  StoreCity: string | null;
  StoreCountryName: string | null;
}


export class ShopifyShopUrls {
  Id?: string | null;
  GalleryId?: string | null;
  AccessTokken?: string | null;
  IsActive!: boolean;
  CreatedDate!: string;
  StoreUrl!: string;
  Gallery?: string | null;
  AdminPanelUrl?: string | null;
  WebhookId!: number;
  ShopDomain!: string;
}

/**
 * Represents Shopify OAuth token information
 */
export class ShopifyOAuthToken {
  Id!: string;
  CreatedDate!: Date;
}